import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { isQuotaError, quotaErrorPayload } from "@/lib/subscriptionApi";
import UpgradeModal from "@/components/UpgradeModal";

const FIELDS = [
  { key: "past_projects", label: "Past Projects Executed", placeholder: "Projects done with this or similar accounts — include scope, year, outcomes" },
  { key: "business_units_engaged", label: "Business Units / Products Engaged With", placeholder: "Which BUs have you previously worked with or pitched?" },
  { key: "existing_relationships", label: "Existing / Past Relationships", placeholder: "Contacts, stakeholders, previous colleagues inside the account" },
  { key: "testimonials_case_studies", label: "Testimonials / References / Case Studies", placeholder: "Quotes, outcomes, case studies you can cite credibly" },
  { key: "known_pain_points", label: "Known Customer-Specific Pain Points", placeholder: "Insider pain points heard from contacts, industry rumours, job postings" },
  { key: "persona_kpis", label: "Known KPIs of Target Personas", placeholder: "What metrics does the VP Engineering / CDO actually care about?" },
  { key: "cultural_insights", label: "Cultural Insights", placeholder: "Decision-making style, vendor preferences, geography nuances" },
  { key: "past_proposals", label: "Past Proposals / Wins / Losses", placeholder: "Prior RFP outcomes, why wins or losses happened" },
  { key: "internal_strengths", label: "Internal Strengths Mapped to This Account", placeholder: "CoEs, certifications, partnerships, relevant scale" },
  { key: "insider_knowledge", label: "Insider Knowledge (non-confidential, high-level)", placeholder: "Board priorities, budget cycles, org changes" },
];

const emptyForm = Object.fromEntries(FIELDS.map((f) => [f.key, ""]));

export const EnhanceDialog = ({ open, onOpenChange, reportId, initial, onStarted }) => {
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [upgrade, setUpgrade] = useState(null);

  useEffect(() => {
    if (open) setForm({ ...emptyForm, ...(initial || {}) });
  }, [open, initial]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    const hasAny = Object.values(form).some((v) => v && v.trim());
    if (!hasAny) {
      toast.warning("Provide at least one primary-data input", {
        description: "RADAR can still proceed but the enhancement will be marked low-relevance.",
      });
    }
    setSubmitting(true);
    try {
      await api.post(`/reports/${reportId}/enhance`, form);
      toast.success("Enhancement started", { description: "Compiling primary-data amplified intelligence…" });
      onStarted?.();
      onOpenChange(false);
    } catch (err) {
      if (isQuotaError(err)) {
        setUpgrade(quotaErrorPayload(err));
        onOpenChange(false);
      } else {
        toast.error("Could not start enhancement", { description: err?.response?.data?.detail || err.message });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl bg-[#141414] border border-[#262626] rounded-sm text-white p-0 overflow-hidden shadow-[0_24px_64px_-12px_rgba(0,0,0,0.8)]"
        data-testid="enhance-dialog"
      >
        <form onSubmit={onSubmit}>
          <DialogHeader className="px-6 py-4 border-b border-[#262626]">
            <DialogTitle className="font-display tracking-tight text-white text-xl flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#0A84FF]" strokeWidth={1.8}/>
              Enhance with primary data
            </DialogTitle>
            <div className="kpi-label">Overlay your internal knowledge to produce a hyper-personalised, tagged intelligence pass.</div>
          </DialogHeader>
          <div className="px-6 py-5 max-h-[70vh] overflow-y-auto space-y-4">
            {FIELDS.map((f) => (
              <div key={f.key} className="flex flex-col gap-1.5">
                <label className="kpi-label" htmlFor={`enh-${f.key}`}>{f.label}</label>
                <Textarea
                  id={`enh-${f.key}`}
                  data-testid={`enh-${f.key}-input`}
                  value={form[f.key]}
                  onChange={set(f.key)}
                  placeholder={f.placeholder}
                  rows={2}
                  className="bg-[#0A0A0A] border-[#262626] rounded-sm text-sm text-white placeholder:text-[#525252] focus-visible:ring-1 focus-visible:ring-[#0A84FF] resize-none"
                />
              </div>
            ))}
          </div>
          <DialogFooter className="px-6 py-4 border-t border-[#262626] bg-[#0A0A0A]/50">
            <Button
              type="button"
              onClick={() => onOpenChange(false)}
              data-testid="enh-cancel-btn"
              className="h-9 rounded-sm bg-transparent hover:bg-[#1F1F1F] text-white border border-[#262626] hover:border-[#404040]"
            >
              <span className="text-xs font-mono-data tracking-wider uppercase">Cancel</span>
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="enh-submit-btn"
              className="h-9 px-4 rounded-sm bg-[#0A84FF] hover:bg-[#409CFF] text-white gap-2"
            >
              {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Sparkles className="w-3.5 h-3.5"/>}
              <span className="text-xs font-mono-data tracking-wider uppercase">{submitting ? "Starting…" : "Amplify"}</span>
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
      <UpgradeModal
        open={!!upgrade}
        onClose={() => setUpgrade(null)}
        resource={upgrade?.resource || "enhancements"}
        plan={upgrade?.plan}
        used={upgrade?.used}
        limit={upgrade?.limit}
      />
    </Dialog>
  );
};

export default EnhanceDialog;
